import { Grid, Theme, Typography, Button, TextField, InputAdornment, FormControlLabel, Checkbox, FormGroup } from '@mui/material';
import { makeStyles, createStyles } from '@mui/styles';
import React, { ChangeEvent, KeyboardEvent, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { AppRole, MutationStatusCode, UpdateConvenienceFeesInput } from '../gql-types.generated';
import { selectTenantAccount } from '../features/app/AppSlice';
import {
  selectConvenienceFeesRequestInFlight,
  selectUpdateConvenienceFeesStatus,
} from '../features/account-preferences/AccountPreferencesSlice';

import EditIcon from '@material-ui/icons/Edit';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
      flexFlow: 'nowrap',
    },
    headerContainer: {
      marginBottom: theme.spacing(2),
    },
    helperText: {
      marginTop: theme.spacing(1),
    },
    buttonAction: {
      margin: theme.spacing(1),
    },
    gridItem: {
      margin: theme.spacing(1, 0),
    },
    name: {
      letterSpacing: 1.5,
    },
    inputRoot: {
      '& .MuiOutlinedInput-root': {
        paddingRight: 0,
        width: 120,
      },
      '& .MuiFormHelperText-root': {
        marginLeft: 0,
      },
    },
    adornment: {
      marginLeft: 0,
      padding: '20px 14px',
      background: theme.palette.uxGrey.hover,
    },
    adornedInput: {
      borderRight: `1px solid ${theme.palette.uxGrey.border}`,
    },
    header: {
      maxWidth: 'calc(100% - 76px)',
    },
    editContainer: {
      width: '76px',
      marginRight: theme.spacing(1),
    },
  }),
);

// eslint-disable-next-line @typescript-eslint/naming-convention
export const CONVENIENCE_FEE_VALUE_MAX_LENGTH = 4;

interface ConvenienceFeesSettingsProps {
  networkBusy: boolean;
  viewerAppRole: AppRole | undefined;
  creditCardRate: number;
  maxCreditConvenienceFee: number;
  updateConvenienceFees: (input: UpdateConvenienceFeesInput) => void;
  isCustomerEnabled?: boolean;
}

const merchantAcknowledgmentText =
  'By checking this box, you acknowledge and agree that your business may apply a surcharge to credit card transactions in accordance with applicable card network rules and state laws. You further agree that any surcharge will be clearly disclosed to the cardholder and that you are solely responsible for complying with all applicable laws, regulations, and card network requirements related to surcharging.';

export const ConvenienceFeesSettings: React.FC<ConvenienceFeesSettingsProps> = props => {
  const classes = useStyles();
  const {
    viewerAppRole,
    updateConvenienceFees,
    networkBusy,
    creditCardRate,
    maxCreditConvenienceFee,
    isCustomerEnabled = false,
  } = props;

  const tenantAccount = useSelector(selectTenantAccount);
  const convenienceFeesUpdateStatus = useSelector(selectUpdateConvenienceFeesStatus);
  const convenienceFeesRequestInFlight = useSelector(selectConvenienceFeesRequestInFlight);
  const convertedCreditFee = (creditCardRate / 100).toString();
  const canUserEdit = viewerAppRole === AppRole.Admin;

  const [creditCardFee, setCreditCardFee] = useState<string>(convertedCreditFee);
  const [isEditingMode, setEditingMode] = useState<boolean>(false);
  const [isCreditFeeValid, setCreditFeeValid] = useState<boolean>(true);
  const [merchantAcknowledged, setMerchantAcknowledged] = useState<boolean>(false);

  const resetValues = () => {
    setCreditCardFee(convertedCreditFee);
    setCreditFeeValid(true);
    setMerchantAcknowledged(false);
    setEditingMode(false);
  };

  const openConvenienceFeesDialog = (): void => setEditingMode(true);

  const saveChanges = (): void => {
    const convertedInput: UpdateConvenienceFeesInput = {
      creditCardRateBps: Math.round(parseFloat(creditCardFee) * 100),
      merchantAcknowledged,
    };
    updateConvenienceFees(convertedInput);
    resetValues();
  };

  useEffect(() => {
    if (convenienceFeesUpdateStatus?.code === MutationStatusCode.Success) {
      setEditingMode(false);
    }
  }, [convenienceFeesUpdateStatus]);

  useEffect(() => {
    resetValues();

    setCreditFeeValid(creditCardRate <= Math.round(maxCreditConvenienceFee * 100));
  }, [tenantAccount]);

  const inLoadingState = networkBusy || convenienceFeesRequestInFlight;
  const isSaveDisabled = inLoadingState || !isCreditFeeValid || !merchantAcknowledged || convertedCreditFee === creditCardFee;

  const validateInput = (input: string, maxConvenienceFee: number): boolean => {
    if (!input || !input.trim().length) return false;
    const numberValue = parseFloat(input);
    if (Number.isNaN(numberValue) || numberValue < 0 || numberValue > maxConvenienceFee) return false;
    return true;
  };

  const changeCreditCardFee = (event: ChangeEvent<HTMLInputElement>): void => {
    const value = event.target.value.trim();
    if (value.length > CONVENIENCE_FEE_VALUE_MAX_LENGTH) return;

    setCreditCardFee(value);
    setCreditFeeValid(validateInput(value, maxCreditConvenienceFee));
  };

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => ['e', 'E', '+', '-'].includes(e.key) && e.preventDefault();

  const creditHelperText = (
    <Typography color="error" variant="caption" data-cy="error-message">
      Please enter a value between 0-{maxCreditConvenienceFee}%.
    </Typography>
  );

  const endAdornment = (
    <InputAdornment className={classes.adornment} position="end">
      %
    </InputAdornment>
  );

  return (
    <Grid container spacing={0} className={classes.root} data-cy="convenience-fees" aria-label="Fee Recovery" role="region">
      <Grid
        container
        item
        xs={12}
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        className={classes.headerContainer}
      >
        <Typography variant="subtitle" className={classes.header}>
          Fee Recovery
        </Typography>
        {canUserEdit && !isEditingMode && (
          <div className={classes.editContainer}>
            <Button
              startIcon={<EditIcon />}
              onClick={openConvenienceFeesDialog}
              disabled={inLoadingState}
              size="small"
              color="primary"
              variant="outlined"
              aria-label="Edit fee recovery"
            >
              EDIT
            </Button>
          </div>
        )}
        <Grid item className={classes.helperText} xs={12}>
          <Typography variant="body1">
            {`Add fee recovery to card payments within the Payer Portal. Charge a maximum of ${maxCreditConvenienceFee}% for credit cards.${
              isCustomerEnabled ? ' Fee recovery can be overridden per customer using the Manage Customer screen.' : ''
            }`}
          </Typography>
        </Grid>
      </Grid>
      <Grid container spacing={0}>
        <Grid container spacing={0} item xs={12} md={10} lg={9} tabIndex={0}>
          {/* credit card fee */}
          <Grid container spacing={0} item xs={12} className={classes.gridItem} justifyContent="center">
            <Grid item xs={12} sm={6} lg={4}>
              <Typography variant="button" className={classes.name}>
                FEE RECOVERY %
              </Typography>
            </Grid>
            <Grid item container xs={12} sm={6} lg={8}>
              <Grid item xs={12}>
                {isEditingMode ? (
                  <TextField
                    type="number"
                    size="small"
                    variant="outlined"
                    className={classes.inputRoot}
                    value={creditCardFee}
                    onChange={changeCreditCardFee}
                    onKeyDown={onKeyDown}
                    error={!isCreditFeeValid}
                    disabled={inLoadingState}
                    helperText={!isCreditFeeValid && creditHelperText}
                    InputProps={{ endAdornment }}
                    inputProps={{
                      'aria-label': 'fee recovery percentage for credit card transactions',
                      'aria-describedby': `${isCreditFeeValid ? undefined : 'credit-card-helpertext'}`,
                      step: 0.01,
                      min: 0,
                      max: maxCreditConvenienceFee,
                      className: classes.adornedInput,
                    }}
                    FormHelperTextProps={{ id: 'credit-card-helpertext' }}
                    data-cy="credit-card-fee-input"
                  />
                ) : (
                  <Typography variant="body1">{creditCardFee}%</Typography>
                )}
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        {/* merchant acknowledgment checkbox */}
        {isEditingMode && (
          <Grid container spacing={0} item xs={12}>
            <FormGroup aria-label="Merchant surcharge acknowledgment">
              <FormControlLabel
                control={
                  <Checkbox checked={merchantAcknowledged} color="primary" onChange={e => setMerchantAcknowledged(e.target.checked)} />
                }
                label={
                  <Typography variant="body2" color="textSecondary">
                    {merchantAcknowledgmentText}
                  </Typography>
                }
                labelPlacement="end"
                data-cy="merchant-acknowledged-checkbox"
              />
            </FormGroup>
          </Grid>
        )}
        {/* action buttons */}
        {isEditingMode && (
          <Grid container spacing={0} item xs={12} justifyContent="flex-end">
            <Button size="small" className={classes.buttonAction} color="primary" onClick={resetValues} disabled={inLoadingState}>
              Cancel
            </Button>
            <Button
              size="small"
              className={classes.buttonAction}
              disabled={isSaveDisabled}
              color="primary"
              variant="contained"
              onClick={saveChanges}
            >
              Save
            </Button>
          </Grid>
        )}
      </Grid>
    </Grid>
  );
};
